/**
 * RoamerVisaModule.ts — Module #3: the Roamer Visa™.
 *
 * A platform module (NOT a core engine): travel document and visa
 * organization. It does NOT replace official government information and
 * never presents unofficial information as legal advice — every piece of
 * guidance carries a provenance class and user-facing surfaces carry the
 * disclaimer. Integration rules (module pattern):
 *  • Registers with Roamer Core™ as an EngineAdapter via attachToCore().
 *  • Inbound data arrives ONLY as Core bus events — LocationChanged (with the
 *    real { location: {...} } payload shape) updates the current country —
 *    plus manual entry (visa records, passport, documents, knowledge).
 *    Future government data providers get a registry interface only.
 *  • Outbound: reminders/alerts published as NotificationSent with kind
 *    'visaReminder' — deterministic reminder ids guarantee once-only firing.
 *  • Honesty: days remaining / statuses derive from recorded dates only;
 *    unknown values are null; recommendations come exclusively from recorded
 *    country knowledge. Document storage keeps REFERENCES only (encrypted
 *    storage is a documented future feature).
 *  • Private by default; export / delete supported. Persistence at
 *    nomad:visa:v1 with record-by-record sanitization.
 */

import { roamerCore, type CoreEvent, type EngineAdapter } from '@/services/core';
import { storageManager } from '@/services/storage/StorageManager';
import {
  DEFAULT_VISA_PRIVACY,
  DOCUMENT_KINDS,
  KNOWLEDGE_SECTIONS,
  LEGAL_DISCLAIMER,
  VISA_PROVENANCES,
  type CountryKnowledge,
  type CountryVisaOption,
  type DocumentKind,
  type DocumentRef,
  type EmergencyContact,
  type KnowledgeItem,
  type KnowledgeSection,
  type TravelPlan,
  type VisaDashboard,
  type VisaExport,
  type VisaPrivacy,
  type VisaProfile,
  type VisaRecord,
  type VisaReminder,
  type VisaStatus,
} from './models';
import {
  computePredictiveAlerts,
  computeReminders,
  daysUntil,
  extensionWindowOpen,
  missingDocuments,
} from './reminders';
import { buildTravelPlan } from './planning';

const STORAGE_KEY = 'nomad:visa:v1';
const MODULE_ID = 'roamer-visa-module';
const MAX_HISTORY = 100;
const MAX_DOCUMENTS = 200;
const MAX_FIRED = 500;

function asString(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/** Registry interface for FUTURE government data providers — no implementations. */
export interface GovernmentDataProvider {
  id: string;
  name: string;
  fetchCountryRules(country: string): Promise<KnowledgeItem[]>;
}

const governmentProviders = new Map<string, GovernmentDataProvider>();

export function registerGovernmentDataProvider(p: GovernmentDataProvider): boolean {
  if (governmentProviders.has(p.id)) return false;
  governmentProviders.set(p.id, p);
  return true;
}

export function getGovernmentDataProviders(): GovernmentDataProvider[] {
  return Array.from(governmentProviders.values());
}

interface PersistedState {
  passportCountry: string | null;
  passportExpiresAt: number | null;
  currentCountry: string | null;
  currentVisa: VisaRecord | null;
  visaHistory: VisaRecord[];
  emergencyContacts: EmergencyContact[];
  documents: DocumentRef[];
  knowledge: CountryKnowledge[];
  privacy: VisaPrivacy;
  firedReminderIds: string[];
}

function emptyKnowledge(country: string): CountryKnowledge {
  return {
    country,
    visaRequirements: [],
    entryRules: [],
    extensionOptions: [],
    borderRunInfo: [],
    embassyLocations: [],
    governmentResources: [],
    healthRequirements: [],
    arrivalRequirements: [],
    futurePolicyChanges: [],
    visaOptions: [],
    entryEase: null,
  };
}

export class RoamerVisaModule {
  private passportCountry: string | null = null;
  private passportExpiresAt: number | null = null;
  private currentCountry: string | null = null;
  private currentVisa: VisaRecord | null = null;
  private visaHistory: VisaRecord[] = [];
  private emergencyContacts: EmergencyContact[] = [];
  private documents: DocumentRef[] = [];
  private knowledge: CountryKnowledge[] = [];
  private privacy: VisaPrivacy = { ...DEFAULT_VISA_PRIVACY };
  private firedReminderIds: string[] = [];
  private unsubscribes: (() => void)[] = [];
  private core: typeof roamerCore | null = null;
  private listeners = new Set<() => void>();

  constructor(now: number = Date.now()) {
    this.load(now);
  }

  // ── Core integration (the ONLY communication channel) ──────────────────────

  buildAdapter(): EngineAdapter {
    return {
      id: MODULE_ID,
      name: 'Roamer Visa™',
      version: '1.0.0',
      capabilities: ['visa-tracking', 'smart-reminders', 'country-knowledge', 'travel-planning'],
      healthCheck: async () => true,
    };
  }

  attachToCore(core: typeof roamerCore = roamerCore, now: number = Date.now()): void {
    this.detachFromCore();
    core.registerEngine(this.buildAdapter(), now);
    this.unsubscribes.push(core.on('LocationChanged', (e) => {
      try {
        this.onLocationChanged(e);
      } catch {
        /* a bad payload must never break the bus */
      }
    }, 'module:visa'));
    this.core = core;
  }

  isAttached(): boolean {
    return this.core != null;
  }

  detachFromCore(): void {
    for (const u of this.unsubscribes) u();
    this.unsubscribes = [];
    this.core = null;
  }

  private onLocationChanged(e: CoreEvent): void {
    const raw = (e.payload ?? {}) as Record<string, unknown>;
    // Real Core traffic wraps the location: { location: RoamerLocation }.
    const p = (raw.location != null && typeof raw.location === 'object'
      ? raw.location
      : raw) as Record<string, unknown>;
    const country = asString(p.country);
    if (!country || country === this.currentCountry) return;
    this.currentCountry = country;
    this.afterUpdate(e.at);
  }

  // ── Manual entry APIs ───────────────────────────────────────────────────────

  setPassport(country: string, expiresAt: number, at: number = Date.now()): void {
    this.passportCountry = country;
    this.passportExpiresAt = expiresAt;
    this.afterUpdate(at);
  }

  /** Record the active visa; the previous one moves to history. */
  setVisa(visa: Omit<VisaRecord, 'notes'> & { notes?: string }, at: number = Date.now()): VisaRecord | null {
    if (!(visa.expiresAt > visa.entryDate)) return null;
    if (this.currentVisa) {
      this.visaHistory.push(this.currentVisa);
      if (this.visaHistory.length > MAX_HISTORY) this.visaHistory = this.visaHistory.slice(-MAX_HISTORY);
    }
    this.currentVisa = { ...visa, notes: visa.notes ?? '' };
    this.afterUpdate(at);
    return structuredClone(this.currentVisa);
  }

  clearVisa(at: number = Date.now()): void {
    if (this.currentVisa) {
      this.visaHistory.push(this.currentVisa);
      if (this.visaHistory.length > MAX_HISTORY) this.visaHistory = this.visaHistory.slice(-MAX_HISTORY);
    }
    this.currentVisa = null;
    this.afterUpdate(at);
  }

  addEmergencyContact(contact: Omit<EmergencyContact, 'id'>, at: number = Date.now()): EmergencyContact {
    const full: EmergencyContact = { ...contact, id: `contact-${at}-${this.emergencyContacts.length}` };
    this.emergencyContacts.push(full);
    this.afterUpdate(at);
    return structuredClone(full);
  }

  /** Store a document REFERENCE (never file contents). */
  addDocumentRef(kind: DocumentKind, title: string, ref: string, at: number = Date.now()): DocumentRef | null {
    if (!DOCUMENT_KINDS.includes(kind)) return null;
    const doc: DocumentRef = { id: `doc-${at}-${this.documents.length}`, kind, title, ref, addedAt: at };
    this.documents.push(doc);
    if (this.documents.length > MAX_DOCUMENTS) this.documents = this.documents.slice(-MAX_DOCUMENTS);
    this.afterUpdate(at);
    return structuredClone(doc);
  }

  removeDocumentRef(id: string, at: number = Date.now()): boolean {
    const before = this.documents.length;
    this.documents = this.documents.filter((d) => d.id !== id);
    if (this.documents.length === before) return false;
    this.afterUpdate(at);
    return true;
  }

  // ── Country knowledge (provenance mandatory) ────────────────────────────────

  addKnowledgeItem(
    country: string,
    section: KnowledgeSection,
    item: Omit<KnowledgeItem, 'recordedAt'>,
    at: number = Date.now(),
  ): boolean {
    if (!KNOWLEDGE_SECTIONS.includes(section)) return false;
    if (!VISA_PROVENANCES.includes(item.provenance)) return false;
    if (!asString(item.text)) return false;
    const ck = this.knowledgeFor(country);
    ck[section].push({ ...item, recordedAt: at });
    this.afterUpdate(at);
    return true;
  }

  addVisaOption(country: string, option: CountryVisaOption, at: number = Date.now()): boolean {
    if (!VISA_PROVENANCES.includes(option.provenance)) return false;
    const ck = this.knowledgeFor(country);
    ck.visaOptions.push({ ...option });
    this.afterUpdate(at);
    return true;
  }

  setEntryEase(country: string, ease: number, at: number = Date.now()): boolean {
    if (!(ease >= 1 && ease <= 5)) return false;
    this.knowledgeFor(country).entryEase = ease;
    this.afterUpdate(at);
    return true;
  }

  getCountryKnowledge(country: string): CountryKnowledge | null {
    const ck = this.knowledge.find((k) => k.country === country);
    return ck ? structuredClone(ck) : null;
  }

  private knowledgeFor(country: string): CountryKnowledge {
    let ck = this.knowledge.find((k) => k.country === country);
    if (!ck) {
      ck = emptyKnowledge(country);
      this.knowledge.push(ck);
    }
    return ck;
  }

  // ── Reads (deep-cloned, honest nulls, disclaimer attached) ─────────────────

  private visaStatus(now: number): VisaStatus {
    if (!this.currentVisa) return 'none';
    const days = daysUntil(this.currentVisa.expiresAt, now);
    if (days < 0) return 'expired';
    if (days <= 14) return 'expiringSoon';
    return 'active';
  }

  getProfile(now: number = Date.now()): VisaProfile {
    const nextOptions = this.currentVisa
      ? this.knowledge.find((k) => k.country === this.currentVisa!.country)?.visaOptions ?? []
      : [];
    return structuredClone({
      passportCountry: this.passportCountry,
      passportExpiresAt: this.passportExpiresAt,
      currentCountry: this.currentCountry,
      currentVisa: this.currentVisa,
      visaStatus: this.visaStatus(now),
      daysRemaining: this.currentVisa ? daysUntil(this.currentVisa.expiresAt, now) : null,
      extensionEligible: this.currentVisa?.extensionEligible ?? null,
      nextVisaOptions: nextOptions,
      emergencyContacts: this.emergencyContacts,
    });
  }

  getDashboard(now: number = Date.now()): VisaDashboard {
    const status = this.visaStatus(now);
    const daysRemaining = this.currentVisa ? daysUntil(this.currentVisa.expiresAt, now) : null;
    const passportDays = this.passportExpiresAt != null ? daysUntil(this.passportExpiresAt, now) : null;
    const upcoming: string[] = [];
    if (this.currentVisa && daysRemaining != null && daysRemaining >= 0) {
      upcoming.push(`Visa expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}.`);
      if (extensionWindowOpen(this.currentVisa, now) === true) upcoming.push('Extension window is open.');
    }
    if (passportDays != null && passportDays <= 183 && passportDays >= 0) {
      upcoming.push('Passport renewal recommended (under 6 months validity).');
    }
    return structuredClone({
      status,
      daysRemaining,
      countdownLabel: daysRemaining == null
        ? 'No active visa on record'
        : daysRemaining < 0
          ? 'Visa expired'
          : `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`,
      passportValid: passportDays == null ? null : passportDays > 0,
      passportDaysRemaining: passportDays,
      extensionWindowOpen: this.currentVisa ? extensionWindowOpen(this.currentVisa, now) : null,
      upcomingRequirements: upcoming,
      requiredDocumentsMissing: this.currentVisa ? missingDocuments(this.documents) : [],
      disclaimer: LEGAL_DISCLAIMER,
    });
  }

  /** New reminders + predictive alerts; each id fires once, published via Core. */
  /**
   * Pure read: ALL currently-active reminders/warnings, regardless of whether
   * they have already been fired as one-time notifications. Never mutates
   * state and never publishes — safe for UI renders and diagnostics.
   */
  getActiveReminders(now: number = Date.now()): VisaReminder[] {
    return [
      ...computeReminders({
        visa: this.currentVisa,
        passportExpiresAt: this.passportExpiresAt,
        documents: this.documents,
        now,
      }),
      ...computePredictiveAlerts({
        visa: this.currentVisa,
        knowledge: this.knowledge,
        passportCountry: this.passportCountry,
        now,
      }),
    ];
  }

  checkReminders(now: number = Date.now()): VisaReminder[] {
    const all = [
      ...computeReminders({
        visa: this.currentVisa,
        passportExpiresAt: this.passportExpiresAt,
        documents: this.documents,
        now,
      }),
      ...computePredictiveAlerts({
        visa: this.currentVisa,
        knowledge: this.knowledge,
        passportCountry: this.passportCountry,
        now,
      }),
    ];
    const fresh = all.filter((r) => !this.firedReminderIds.includes(r.id));
    for (const reminder of fresh) {
      this.firedReminderIds.push(reminder.id);
      try {
        this.core?.publish('NotificationSent', MODULE_ID, {
          kind: 'visaReminder',
          reminderId: reminder.id,
          severity: reminder.severity,
          message: reminder.message,
          provenance: reminder.provenance,
        }, now);
      } catch {
        /* announcements are best-effort */
      }
    }
    if (this.firedReminderIds.length > MAX_FIRED) {
      this.firedReminderIds = this.firedReminderIds.slice(-MAX_FIRED);
    }
    if (fresh.length > 0) this.persist();
    return structuredClone(fresh);
  }

  getTravelPlan(preferredCountries: string[] = [], now: number = Date.now()): TravelPlan {
    return structuredClone(buildTravelPlan({
      visa: this.currentVisa,
      knowledge: this.knowledge,
      preferredCountries,
      now,
    }));
  }

  getDocuments(): DocumentRef[] {
    return structuredClone(this.documents);
  }

  getVisaHistory(): VisaRecord[] {
    return structuredClone(this.visaHistory);
  }

  // ── Privacy, export, deletion ───────────────────────────────────────────────

  getPrivacy(): VisaPrivacy {
    return { ...this.privacy };
  }

  updatePrivacy(patch: Partial<VisaPrivacy>): VisaPrivacy {
    this.privacy = { ...this.privacy, ...patch };
    this.persist();
    return this.getPrivacy();
  }

  /** Shared status — private by default. */
  getSharedStatus(now: number = Date.now()): { status: VisaStatus; daysRemaining: number | null } | null {
    if (!this.privacy.shareStatus) return null;
    return { status: this.visaStatus(now), daysRemaining: this.currentVisa ? daysUntil(this.currentVisa.expiresAt, now) : null };
  }

  exportVisa(now: number = Date.now()): VisaExport {
    return structuredClone({
      exportedAt: now,
      profile: this.getProfile(now),
      visaHistory: this.visaHistory,
      countryKnowledge: this.knowledge,
      documents: this.documents,
      privacy: this.privacy,
    });
  }

  deleteAll(at: number = Date.now()): void {
    this.passportCountry = null;
    this.passportExpiresAt = null;
    this.currentCountry = null;
    this.currentVisa = null;
    this.visaHistory = [];
    this.emergencyContacts = [];
    this.documents = [];
    this.knowledge = [];
    this.privacy = { ...DEFAULT_VISA_PRIVACY };
    this.firedReminderIds = [];
    this.persist();
    this.emitChange();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitChange(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch { /* isolated */ }
    }
  }

  private afterUpdate(_at: number): void {
    this.persist();
    this.emitChange();
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const state: PersistedState = {
        passportCountry: this.passportCountry,
        passportExpiresAt: this.passportExpiresAt,
        currentCountry: this.currentCountry,
        currentVisa: this.currentVisa,
        visaHistory: this.visaHistory,
        emergencyContacts: this.emergencyContacts,
        documents: this.documents,
        knowledge: this.knowledge,
        privacy: this.privacy,
        firedReminderIds: this.firedReminderIds,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      storageManager.push('visa', 'state', state);
    } catch {
      /* storage unavailable — keep working in memory */
    }
  }

  /** Called by hydrateVisaFromCloud when a newer cloud copy is available. */
  reloadFromStorage(now = Date.now()): void {
    this.load(now);
    this.emitChange();
  }

  private load(_now: number): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const state = JSON.parse(raw) as Record<string, unknown> | null;
      if (state == null || typeof state !== 'object') return;

      this.passportCountry = asString(state.passportCountry);
      this.passportExpiresAt = typeof state.passportExpiresAt === 'number' ? state.passportExpiresAt : null;
      this.currentCountry = asString(state.currentCountry);

      const validVisa = (v: unknown): v is VisaRecord =>
        v != null && typeof v === 'object' &&
        typeof (v as VisaRecord).country === 'string' &&
        typeof (v as VisaRecord).type === 'string' &&
        typeof (v as VisaRecord).entryDate === 'number' &&
        typeof (v as VisaRecord).expiresAt === 'number' &&
        typeof (v as VisaRecord).extensionEligible === 'boolean';
      this.currentVisa = validVisa(state.currentVisa)
        ? {
            ...state.currentVisa,
            exitDate: typeof state.currentVisa.exitDate === 'number' ? state.currentVisa.exitDate : null,
            extensionWindowDays: typeof state.currentVisa.extensionWindowDays === 'number' ? state.currentVisa.extensionWindowDays : null,
            notes: typeof state.currentVisa.notes === 'string' ? state.currentVisa.notes : '',
          }
        : null;
      this.visaHistory = (Array.isArray(state.visaHistory) ? state.visaHistory : [])
        .filter(validVisa)
        .slice(-MAX_HISTORY);

      this.emergencyContacts = (Array.isArray(state.emergencyContacts) ? state.emergencyContacts : [])
        .filter((c): c is EmergencyContact =>
          c != null && typeof c === 'object' &&
          typeof c.id === 'string' && typeof c.name === 'string' && typeof c.phone === 'string');

      this.documents = (Array.isArray(state.documents) ? state.documents : [])
        .filter((d): d is DocumentRef =>
          d != null && typeof d === 'object' &&
          typeof d.id === 'string' && DOCUMENT_KINDS.includes(d.kind) &&
          typeof d.ref === 'string' && typeof d.addedAt === 'number')
        .slice(-MAX_DOCUMENTS);

      const validItem = (i: unknown): i is KnowledgeItem =>
        i != null && typeof i === 'object' &&
        typeof (i as KnowledgeItem).text === 'string' &&
        VISA_PROVENANCES.includes((i as KnowledgeItem).provenance) &&
        typeof (i as KnowledgeItem).recordedAt === 'number';
      this.knowledge = (Array.isArray(state.knowledge) ? state.knowledge : [])
        .filter((k): k is Record<string, unknown> => k != null && typeof k === 'object' && typeof (k as { country?: unknown }).country === 'string')
        .map((k) => {
          const ck = emptyKnowledge(k.country as string);
          for (const section of KNOWLEDGE_SECTIONS) {
            ck[section] = (Array.isArray(k[section]) ? (k[section] as unknown[]) : []).filter(validItem);
          }
          ck.visaOptions = (Array.isArray(k.visaOptions) ? (k.visaOptions as unknown[]) : [])
            .filter((o): o is CountryVisaOption =>
              o != null && typeof o === 'object' &&
              typeof (o as CountryVisaOption).type === 'string' &&
              VISA_PROVENANCES.includes((o as CountryVisaOption).provenance));
          ck.entryEase = typeof k.entryEase === 'number' && k.entryEase >= 1 && k.entryEase <= 5 ? k.entryEase : null;
          return ck;
        });

      const priv = (state.privacy ?? {}) as Record<string, unknown>;
      this.privacy = { shareStatus: priv.shareStatus === true };
      this.firedReminderIds = (Array.isArray(state.firedReminderIds) ? state.firedReminderIds : [])
        .filter((id): id is string => typeof id === 'string')
        .slice(-MAX_FIRED);
    } catch {
      /* corrupt state — start fresh rather than crash */
    }
  }
}

/** App-wide singleton. Call attachToCore() during app bootstrap (after roamerCore.initialize()). */
export const roamerVisaModule = new RoamerVisaModule();

/**
 * Boot hydration: fetches the cloud copy of the Visa™ state if it is
 * strictly newer than the last local write, writes it to localStorage, and
 * signals the module to reload.  Fire-and-forget — boot never blocks the network.
 */
export async function hydrateVisaFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<unknown>('visa', 'state');
  if (!cloud) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('nomad:visa:v1', JSON.stringify(cloud));
    }
  } catch { return false; }
  roamerVisaModule.reloadFromStorage();
  return true;
}
